import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

interface ChartsProps {
  minTitanGuarantee: number;
  currentRevenue: number;
  currentFund: number;
}

export const DashboardCharts: React.FC<ChartsProps> = ({ minTitanGuarantee, currentRevenue, currentFund }) => {
  const trendCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const distCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const trendChartInstance = useRef<Chart | null>(null);
  const distChartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!trendCanvasRef.current || !distCanvasRef.current) return;

    // Destroy existing chart instances
    if (trendChartInstance.current) trendChartInstance.current.destroy();
    if (distChartInstance.current) distChartInstance.current.destroy();

    // 1. Trend Chart
    const baseActual = currentFund / 1000;
    const actualData = [baseActual * 0.5, baseActual * 0.8, baseActual, baseActual * 1.2, baseActual * 1.5, baseActual * 1.1];
    const guaranteeData = Array(6).fill(minTitanGuarantee);

    trendChartInstance.current = new Chart(trendCanvasRef.current, {
      type: 'line',
      data: {
        labels: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6'],
        datasets: [
          {
            label: 'Dự phóng Phân bổ thực tế',
            data: actualData,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            fill: true
          },
          {
            label: 'Mức bảo chứng (3%)',
            data: guaranteeData,
            borderColor: '#f59e0b',
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: { y: { beginAtZero: true, ticks: { callback: (val) => '$' + val } } }
      }
    });

    // 2. Distribution Chart
    const otherFund = currentRevenue - currentFund;
    distChartInstance.current = new Chart(distCanvasRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Quỹ Titan+Pioneer (8%)', 'Hệ thống khác (92%)'],
        datasets: [{
          data: [currentFund, otherFund],
          backgroundColor: ['#f59e0b', '#e5e7eb'],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: { legend: { position: 'bottom' } }
      }
    });

    return () => {
      if (trendChartInstance.current) trendChartInstance.current.destroy();
      if (distChartInstance.current) distChartInstance.current.destroy();
    };
  }, [minTitanGuarantee, currentRevenue, currentFund]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4">Biểu đồ dự phóng Thưởng Titan (6 tháng)</h4>
        <div className="relative h-64 w-full">
          <canvas ref={trendCanvasRef}></canvas>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4">Phân bổ Net Revenue</h4>
        <div className="relative h-64 w-full flex justify-center">
          <canvas ref={distCanvasRef}></canvas>
        </div>
      </div>
    </div>
  );
};